import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/hooks/useAuth';
import { useMe } from '@/lib/hooks/useMe';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { LogOut, LogIn, Loader2, AlertCircle, BarChart3 } from 'lucide-react';
import { useEffect } from 'react';

/**
 * UserProfile component displayed at the bottom of the sidebar.
 * Shows user info when authenticated, or sign-in button when not.
 */
export function UserProfile() {
  const { user, isAuthenticated, isLoading, error, login, logout, clearError } = useAuth();
  const { data: me } = useMe();

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // Loading state
  if (isLoading) {
    return (
      <div className="border-t border-gray-200 p-2 bg-card">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Authenticated state
  if (isAuthenticated && user) {
    // Get initials for avatar (first letter of first two words)
    const initials = user.name
      .split(' ')
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();

    return (
      <div className="border-t border-gray-200 bg-card">
        {/* Statistiche link (admin/global_admin only) — full-width row above user info */}
        {me && me.role !== 'user' && (
          <Link
            to="/admin/usage"
            className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors border-b border-gray-200"
          >
            <BarChart3 size={16} />
            <span>Statistiche</span>
          </Link>
        )}

        <div className="p-2">
          {/* Error message if present */}
          {error && (
            <Alert variant="danger" className="mb-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* User info */}
          <div className="flex items-center gap-3">
            {/* Avatar with initials */}
            <div className="h-10 w-10 rounded-full bg-primary text-white font-semibold flex items-center justify-center text-sm shadow-sm">
              {initials}
            </div>

            {/* User details */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>

            {/* Logout button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-danger hover:text-danger-700 hover:bg-danger-50"
              title="Esci"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated state
  return (
    <div className="border-t border-gray-200 p-2 bg-card">
      {/* Error message if present */}
      {error && (
        <Alert variant="danger" className="mb-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Sign in button */}
      <Button
        variant="primary"
        onClick={login}
        disabled={isLoading}
        className="w-full"
      >
        <LogIn className="mr-2 h-4 w-4" />
        Accedi con Microsoft
      </Button>
    </div>
  );
}
